import React, { useEffect, useMemo, useState } from 'react';
import Column from '@amzn/meridian/column';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';
import Button from '@amzn/meridian/button';
import Card from '@amzn/meridian/card';
import Tag from '@amzn/meridian/tag';
import Box from '@amzn/meridian/box';
import SearchField from '@amzn/meridian/search-field';
import Table, { TableRow, TableCell } from '@amzn/meridian/table';
import SectionNav from '../components/SectionNav';
import Avatar from '../components/Avatar';
import { getSupplierControlService } from '../services';
import type {
    ControlSite,
    ControlRequest,
    ControlUser,
    OnboardedSupplier,
    UserType,
} from '../services/types';

// Supplier Control — internal access management. Ported from the dscs
// prototypes (Suppliers directory + Approval Queue + Supplier Review), wired to
// the SupplierControlService. Data is empty until the backend is built.

type ControlView = 'suppliers' | 'queue' | 'review' | 'users';

const CONTROL_NAV: { id: ControlView; label: string }[] = [
    { id: 'suppliers', label: 'Onboarded Suppliers' },
    { id: 'queue', label: 'Approval Queue' },
    { id: 'users', label: 'Users' },
];

function fmtDate(iso: string): string {
    if (!iso) return '—';
    const d = new Date(iso);
    return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString();
}

// Shared empty-state row spanning the full table width.
function EmptyRow({ span, message }: { span: number; message: string }) {
    return (
        <TableRow>
            <TableCell columnSpan={span} alignmentHorizontal="center">
                <Box spacingInset="500">
                    <Text color="secondary">{message}</Text>
                </Box>
            </TableCell>
        </TableRow>
    );
}

// ── Onboarded suppliers directory ─────────────────────────────────────────────
function SuppliersView({ suppliers }: { suppliers: OnboardedSupplier[] }) {
    const [q, setQ] = useState('');
    const query = q.trim().toLowerCase();
    const shown = suppliers.filter(
        (s) =>
            query === '' ||
            s.company.toLowerCase().includes(query) ||
            s.email.toLowerCase().includes(query),
    );
    const headers = ['Supplier', 'Sites', 'Status', 'Onboarded'];

    return (
        <Column spacing="400">
            <Row alignmentHorizontal="justify" alignmentVertical="center" wrap="down" spacing="300">
                <Tag type="success">{`${suppliers.length} onboarded`}</Tag>
                <SearchField
                    value={q}
                    onChange={setQ}
                    onSubmit={() => undefined}
                    placeholder="Search by supplier or email…"
                    width={320}
                    label="Search onboarded suppliers"
                />
            </Row>
            <Card spacingInset="none">
                <Table headerRows={1} showDividers showStripes spacing="medium">
                    <TableRow>
                        {headers.map((h) => (
                            <TableCell key={h}>{h}</TableCell>
                        ))}
                    </TableRow>
                    {shown.length === 0 ? (
                        <EmptyRow
                            span={headers.length}
                            message={
                                suppliers.length === 0
                                    ? 'No onboarded suppliers yet. Approve sign-ups from the Approval Queue.'
                                    : 'No matching suppliers.'
                            }
                        />
                    ) : (
                        shown.map((s) => (
                            <TableRow key={s.id} highlightOnHover>
                                <TableCell>
                                    <Row spacing="300" alignmentVertical="center">
                                        <Avatar name={s.company} seed={s.id} />
                                        <Column spacing="100">
                                            <Text type="b300">{s.company}</Text>
                                            <Text
                                                type="b100"
                                                color="secondary"
                                                fontFamily="amazonEmberMonospace"
                                            >
                                                {s.email}
                                            </Text>
                                        </Column>
                                    </Row>
                                </TableCell>
                                <TableCell>
                                    <Tag type="theme">{`${s.sites} site${s.sites === 1 ? '' : 's'}`}</Tag>
                                </TableCell>
                                <TableCell>
                                    <Tag type="success">Active</Tag>
                                </TableCell>
                                <TableCell>
                                    <Text color="secondary">{fmtDate(s.onboarded)}</Text>
                                </TableCell>
                            </TableRow>
                        ))
                    )}
                </Table>
            </Card>
        </Column>
    );
}

// ── Approval queue ────────────────────────────────────────────────────────────
function QueueView({
    requests,
    approvingId,
    onApprove,
}: {
    requests: ControlRequest[];
    approvingId: string | null;
    onApprove: (id: string) => void;
}) {
    const [q, setQ] = useState('');
    const query = q.trim().toLowerCase();
    const shown = requests.filter(
        (r) =>
            query === '' ||
            r.email.toLowerCase().includes(query) ||
            r.supplier.toLowerCase().includes(query),
    );
    const headers = ['Supplier', 'Sites', 'Status', 'Submitted', ''];

    return (
        <Column spacing="400">
            <SearchField
                value={q}
                onChange={setQ}
                onSubmit={() => undefined}
                placeholder="Search by email or supplier…"
                width={360}
                label="Search approval queue"
            />
            <Card spacingInset="none">
                <Table headerRows={1} showDividers showStripes spacing="medium">
                    <TableRow>
                        {headers.map((h, i) => (
                            <TableCell key={h || `blank-${i}`}>{h}</TableCell>
                        ))}
                    </TableRow>
                    {shown.length === 0 ? (
                        <EmptyRow span={headers.length} message="No pending requests to show yet." />
                    ) : (
                        shown.map((r) => (
                            <TableRow key={r.id} highlightOnHover>
                                <TableCell>
                                    <Row spacing="300" alignmentVertical="center">
                                        <Avatar name={r.supplier} seed={r.id} />
                                        <Column spacing="100">
                                            <Text type="b300">{r.supplier}</Text>
                                            <Text
                                                type="b100"
                                                color="secondary"
                                                fontFamily="amazonEmberMonospace"
                                            >
                                                {r.email}
                                            </Text>
                                        </Column>
                                    </Row>
                                </TableCell>
                                <TableCell>
                                    <Tag type="theme">{`${r.sites} site${r.sites > 1 ? 's' : ''}`}</Tag>
                                </TableCell>
                                <TableCell>
                                    {r.verified ? (
                                        <Tag type="warning">Email verified · Awaiting approval</Tag>
                                    ) : (
                                        <Tag type="neutral">Awaiting email verification</Tag>
                                    )}
                                </TableCell>
                                <TableCell>
                                    <Text color="secondary">{r.submitted}</Text>
                                </TableCell>
                                <TableCell alignmentHorizontal="end">
                                    {r.verified ? (
                                        <Button
                                            type="primary"
                                            size="small"
                                            disabled={approvingId !== null}
                                            onClick={() => onApprove(r.id)}
                                        >
                                            {approvingId === r.id ? 'Approving…' : 'Approve'}
                                        </Button>
                                    ) : (
                                        <Text color="secondary">Not ready</Text>
                                    )}
                                </TableCell>
                            </TableRow>
                        ))
                    )}
                </Table>
            </Card>
        </Column>
    );
}

// ── Supplier review (per-site grant decisions for one pending login) ──────────
type Decision = 'pending' | 'approved' | 'rejected';

function ReviewView({
    request,
    sites,
    onBack,
}: {
    request: ControlRequest | null;
    sites: ControlSite[];
    onBack: () => void;
}) {
    const allTo = (d: Decision): Record<string, Decision> =>
        Object.fromEntries(sites.map((s) => [s.id, d]));
    const [decisions, setDecisions] = useState<Record<string, Decision>>(allTo('pending'));
    const [done, setDone] = useState(false);
    useEffect(() => setDecisions(Object.fromEntries(sites.map((s) => [s.id, 'pending']))), [sites]);

    const set = (id: string, d: Decision) => setDecisions((prev) => ({ ...prev, [id]: d }));
    const allDecided = sites.length > 0 && sites.every((s) => decisions[s.id] !== 'pending');
    const approvedCount = sites.filter((s) => decisions[s.id] === 'approved').length;

    if (!request) {
        return (
            <Card>
                <Column alignmentHorizontal="center" spacing="300" spacingInset="500">
                    <Text color="secondary">No request selected.</Text>
                    <Button type="link" onClick={onBack}>
                        Back to queue
                    </Button>
                </Column>
            </Card>
        );
    }

    if (done) {
        return (
            <Card>
                <Column alignmentHorizontal="center" spacing="400" spacingInset="600">
                    <Tag type="success">Decision recorded</Tag>
                    <Text color="secondary" alignment="center">
                        {request.email} was granted access to {approvedCount} of {sites.length}{' '}
                        site(s).
                    </Text>
                    <Button type="secondary" onClick={onBack}>
                        Back to queue
                    </Button>
                </Column>
            </Card>
        );
    }

    return (
        <Column spacing="400" maxWidth={860}>
            <Row>
                <Button type="link" onClick={onBack}>
                    ← Back to queue
                </Button>
            </Row>

            <Card>
                <Column spacing="200" spacingInset="500">
                    <Text type="h300">{request.supplier}</Text>
                    <Row spacing="200" alignmentVertical="center">
                        <Tag type="warning">Pending login</Tag>
                        <Text color="secondary" fontFamily="amazonEmberMonospace">
                            {request.email}
                        </Text>
                    </Row>
                </Column>
            </Card>

            <Card spacingInset="none">
                <Column spacing="none">
                    <Box spacingInset="400 500" type="fill" backgroundColor="subdued">
                        <Row alignmentHorizontal="justify" alignmentVertical="center">
                            <Text type="b300">Grant site access</Text>
                            <Text color="secondary">
                                {approvedCount} of {sites.length} granted
                            </Text>
                        </Row>
                    </Box>
                    {sites.length === 0 ? (
                        <Box spacingInset="500">
                            <Text color="secondary" alignment="center">
                                No sites to show yet.
                            </Text>
                        </Box>
                    ) : (
                        sites.map((s) => {
                            const d = decisions[s.id];
                            return (
                                <Box key={s.id} spacingInset="400 500">
                                    <Row alignmentHorizontal="justify" alignmentVertical="center">
                                        <Column spacing="100">
                                            <Row spacing="200" alignmentVertical="center">
                                                <Text type="b300">{s.name}</Text>
                                                <Tag type="neutral">{s.code}</Tag>
                                            </Row>
                                            <Text color="secondary">{s.location}</Text>
                                        </Column>
                                        <Row spacing="200" alignmentVertical="center">
                                            {d !== 'pending' ? (
                                                <Tag type={d === 'approved' ? 'success' : 'error'}>
                                                    {d === 'approved' ? 'Access: Yes' : 'Access: No'}
                                                </Tag>
                                            ) : null}
                                            <Button
                                                type={d === 'approved' ? 'primary' : 'secondary'}
                                                size="small"
                                                onClick={() => set(s.id, 'approved')}
                                            >
                                                Yes
                                            </Button>
                                            <Button
                                                type={d === 'rejected' ? 'primary' : 'secondary'}
                                                size="small"
                                                onClick={() => set(s.id, 'rejected')}
                                            >
                                                No
                                            </Button>
                                        </Row>
                                    </Row>
                                </Box>
                            );
                        })
                    )}
                    {sites.length > 0 ? (
                        <Box spacingInset="400 500" type="fill" backgroundColor="subdued">
                            <Row spacing="200">
                                <Button
                                    type="secondary"
                                    size="small"
                                    onClick={() => setDecisions(allTo('approved'))}
                                >
                                    Approve all
                                </Button>
                                <Button
                                    type="secondary"
                                    size="small"
                                    onClick={() => setDecisions(allTo('rejected'))}
                                >
                                    Reject all
                                </Button>
                            </Row>
                        </Box>
                    ) : null}
                </Column>
            </Card>

            <Row alignmentHorizontal="end">
                <Button
                    type="primary"
                    disabled={!allDecided}
                    onClick={() => allDecided && setDone(true)}
                >
                    {allDecided ? 'Confirm decisions' : 'Decide all sites first'}
                </Button>
            </Row>
        </Column>
    );
}

// ── Users (ops view: supplier vs employee) ───────────────────────────────────
const USER_TYPE_FILTERS: { id: UserType | 'all'; label: string }[] = [
    { id: 'all', label: 'All' },
    { id: 'supplier', label: 'Suppliers' },
    { id: 'employee', label: 'Employees' },
];

function UsersView({ users }: { users: ControlUser[] }) {
    const [q, setQ] = useState('');
    const [typeFilter, setTypeFilter] = useState<UserType | 'all'>('all');

    const query = q.trim().toLowerCase();
    const shown = users.filter(
        (u) =>
            (typeFilter === 'all' || u.type === typeFilter) &&
            (query === '' ||
                u.email.toLowerCase().includes(query) ||
                u.username.toLowerCase().includes(query)),
    );
    const headers = ['User', 'Type', 'Roles', 'Status', 'Created'];

    const supplierCount = users.filter((u) => u.type === 'supplier').length;
    const employeeCount = users.filter((u) => u.type === 'employee').length;

    return (
        <Column spacing="400">
            <Row spacing="200" wrap="down">
                <Tag type="theme">{`${supplierCount} suppliers`}</Tag>
                <Tag type="neutral">{`${employeeCount} employees`}</Tag>
            </Row>
            <Row alignmentHorizontal="justify" alignmentVertical="center" wrap="down" spacing="300">
                <Row spacing="200">
                    {USER_TYPE_FILTERS.map((f) => (
                        <Button
                            key={f.id}
                            type={typeFilter === f.id ? 'primary' : 'tertiary'}
                            size="small"
                            onClick={() => setTypeFilter(f.id)}
                        >
                            {f.label}
                        </Button>
                    ))}
                </Row>
                <SearchField
                    value={q}
                    onChange={setQ}
                    onSubmit={() => undefined}
                    placeholder="Search by email or username…"
                    width={300}
                    label="Search users"
                />
            </Row>
            <Card spacingInset="none">
                <Table headerRows={1} showDividers spacing="small">
                    <TableRow>
                        {headers.map((h) => (
                            <TableCell key={h}>{h}</TableCell>
                        ))}
                    </TableRow>
                    {shown.length === 0 ? (
                        <EmptyRow span={headers.length} message="No users to show yet." />
                    ) : (
                        shown.map((u) => (
                            <TableRow key={u.username}>
                                <TableCell>
                                    <Column spacing="100">
                                        <Text type="b300">{u.email}</Text>
                                        <Text color="secondary" fontFamily="amazonEmberMonospace">
                                            {u.username}
                                        </Text>
                                    </Column>
                                </TableCell>
                                <TableCell>
                                    <Tag type={u.type === 'employee' ? 'neutral' : 'theme'}>
                                        {u.type === 'employee' ? 'Employee' : 'Supplier'}
                                    </Tag>
                                </TableCell>
                                <TableCell>
                                    <Text color="secondary">
                                        {u.roles.length ? u.roles.join(', ') : '—'}
                                    </Text>
                                </TableCell>
                                <TableCell>
                                    <Tag type={u.enabled ? 'success' : 'neutral'}>
                                        {u.enabled ? u.status : 'Disabled'}
                                    </Tag>
                                </TableCell>
                                <TableCell>
                                    <Text color="secondary">{u.created}</Text>
                                </TableCell>
                            </TableRow>
                        ))
                    )}
                </Table>
            </Card>
        </Column>
    );
}

// ── Main Control area ─────────────────────────────────────────────────────────
export default function SupplierControlScreen() {
    const [view, setView] = useState<ControlView>('suppliers');
    const [suppliers, setSuppliers] = useState<OnboardedSupplier[]>([]);
    const [requests, setRequests] = useState<ControlRequest[]>([]);
    const [users, setUsers] = useState<ControlUser[]>([]);
    const [reviewId, setReviewId] = useState<string | null>(null);
    const [approvingId, setApprovingId] = useState<string | null>(null);
    const [approveError, setApproveError] = useState<string | null>(null);

    const approve = (id: string) => {
        setApprovingId(id);
        setApproveError(null);
        getSupplierControlService()
            .approveRequest(id)
            .then((refreshed) => setRequests(refreshed))
            .catch((e) =>
                setApproveError(e instanceof Error ? e.message : 'Failed to approve request.'),
            )
            .finally(() => setApprovingId(null));
    };

    useEffect(() => {
        let live = true;
        const svc = getSupplierControlService();
        Promise.all([svc.listOnboardedSuppliers(), svc.listRequests(), svc.listUsers()])
            .then(([s, r, u]) => {
                if (live) {
                    setSuppliers(s);
                    setRequests(r);
                    setUsers(u);
                }
            })
            .catch(() => {
                if (live) {
                    setSuppliers([]);
                    setRequests([]);
                    setUsers([]);
                }
            });
        return () => {
            live = false;
        };
    }, []);

    const reviewRequest = useMemo(
        () => requests.find((r) => r.id === reviewId) ?? null,
        [requests, reviewId],
    );
    // Per-site review needs the sites model, which is a separate follow-up; no
    // sites are available yet, so the review screen shows its empty state.
    const reviewSites: ControlSite[] = useMemo(() => [], []);

    const pendingCount = requests.filter((r) => r.verified).length;

    const titleByView: Record<ControlView, { title: string; subtitle: string }> = {
        suppliers: {
            title: 'Onboarded Suppliers',
            subtitle: 'Approved suppliers with active gateway access',
        },
        queue: {
            title: 'Approval Queue',
            subtitle: "Pending supplier logins — grant access to the supplier's sites",
        },
        review: { title: 'Supplier Review', subtitle: 'Grant per-site access for a pending login' },
        users: {
            title: 'Users',
            subtitle: 'All accounts by type — suppliers (password) and employees (Midway SSO)',
        },
    };

    const activeNav = view === 'review' ? 'queue' : view;

    return (
        <Row alignmentVertical="top" spacing="500" width="100%">
            <SectionNav
                items={CONTROL_NAV}
                active={activeNav}
                onSelect={(id) => {
                    setView(id);
                    setReviewId(null);
                }}
            />

            <Column spacing="400" width="100%">
                <Row alignmentHorizontal="justify" alignmentVertical="center" wrap="down" spacing="300">
                    <Column spacing="100">
                        <Text type="h400">{titleByView[view].title}</Text>
                        <Text color="secondary">{titleByView[view].subtitle}</Text>
                    </Column>
                    {view === 'queue' && pendingCount > 0 ? (
                        <Tag type="warning">{`${pendingCount} pending`}</Tag>
                    ) : (
                        <Tag type="neutral">Internal</Tag>
                    )}
                </Row>

                {view === 'suppliers' ? <SuppliersView suppliers={suppliers} /> : null}
                {view === 'queue' ? (
                    <Column spacing="300">
                        {approveError ? (
                            <Text color="error">{approveError}</Text>
                        ) : null}
                        <QueueView
                            requests={requests}
                            approvingId={approvingId}
                            onApprove={approve}
                        />
                    </Column>
                ) : null}
                {view === 'review' ? (
                    <ReviewView
                        request={reviewRequest}
                        sites={reviewSites}
                        onBack={() => {
                            setView('queue');
                            setReviewId(null);
                        }}
                    />
                ) : null}
                {view === 'users' ? <UsersView users={users} /> : null}
            </Column>
        </Row>
    );
}
